<template>
    <UModal v-model:open="isOpen" class="sm:max-w-4xl" :dismissible="false">
        <template #content>
        <UCard class="overflow-hidden max-h-[85dvh] flex flex-col" :ui="{ body: 'flex-1 overflow-y-auto min-h-0' }">
            <template #header>
                <div class="flex items-center justify-between">
                    <div>
                        <h3 class="text-lg font-semibold">{{ sheet.name }}</h3>
                        <p class="text-sm text-slate-500 mt-1">
                            {{ stepDescription }}
                        </p>
                    </div>
                    <div class="flex items-center space-x-3">
                        <!-- Step indicator -->
                        <div class="flex items-center space-x-1">
                            <div v-for="(s, i) in steps" :key="s" :class="[
                                'w-2 h-2 rounded-full transition-colors',
                                currentStepIndex === i ? 'bg-blue-500' :
                                    currentStepIndex > i ? 'bg-green-500' : 'bg-slate-300'
                            ]" />
                        </div>
                        <UButton color="neutral" variant="ghost" icon="i-ion-close" @click="handleClose" size="sm" />
                    </div>
                </div>
            </template>

            <!-- Step Content -->
            <div class="min-h-[400px]">
                <div v-if="loadingSessions" class="p-4 text-sm text-slate-500">Looking for saved imports...</div>
                <div v-else-if="sessionLookupFailed" class="p-4 space-y-3">
                    <p class="text-sm text-slate-600">Could not check your saved imports. Retry before starting another upload.</p>
                    <UButton @click="loadSavedSessions">Retry</UButton>
                </div>
                <div v-else-if="savedSessions.length && !session" class="p-4 space-y-3">
                    <p class="text-sm text-slate-600">Continue a saved import, or start a new one.</p>
                    <div v-for="saved in savedSessions" :key="saved.id" class="flex items-center justify-between border rounded p-3 gap-3">
                        <span class="text-sm">{{ saved.originalFilename || 'Pasted data' }} · {{ saved.stage }}</span>
                        <UButton @click="resumeSession(saved)" :disabled="busy">Continue</UButton>
                    </div>
                    <UButton variant="outline" @click="savedSessions = []">New import</UButton>
                </div>
                <div v-else-if="processing" class="flex flex-col items-center justify-center min-h-[400px] gap-3">
                    <UIcon name="i-heroicons-arrow-path" class="w-6 h-6 animate-spin text-blue-500" />
                    <p class="text-sm text-slate-600">{{ progressDescription }}</p>
                    <p class="text-xs text-slate-500">{{ transferProgress ? 'Closing pauses the file transfer. Reselect the same file later to resume.' : 'You can close this window. The uploaded file keeps processing in the background.' }}</p>
                </div>
                <div v-else-if="session?.stage === 'failed' && session.failureRetry !== 'mapping'" class="p-4 space-y-3 text-sm text-red-600">
                    <p>This file could not be read. Your uploaded file has been kept.</p>
                    <UButton v-if="session.failureRetry === 'parse'" :disabled="busy" :loading="busy" @click="retryParsing">Retry reading file</UButton>
                </div>
                <template v-else>
                    <p v-if="session?.stage === 'uploading'" class="px-4 pt-4 text-sm text-slate-600">
                        Reselect {{ session.originalFilename || 'the original pasted data' }} to resume the interrupted transfer. The file contents must match.
                    </p>
                    <SharedCSVImporterUploadStep v-if="step === 'upload'" v-model:raw-text="rawText" v-model:filename="filename"
                        :fields="sheet.fields" :hint="sheet.hint" :disabled="busy" @file="selectFile" />
                    <template v-else-if="step === 'mapping'">
                        <p v-if="session?.failureRetry === 'mapping'" class="px-4 pt-4 text-sm text-amber-700">Validation failed. Review the mapping and select Next to validate again. As with going back to mapping, this resets row edits and removals.</p>
                        <UFormField v-if="(session?.worksheets.length ?? 0) > 1" label="Worksheet" class="px-4 pt-4">
                            <USelect :model-value="session?.selectedWorksheet" :items="session?.worksheets.map(sheet => ({ label: sheet.name, value: sheet.index }))"
                                :disabled="busy" @update:model-value="changeWorksheet" />
                        </UFormField>
                        <SharedCSVImporterMappingStep :inert="busy" :headers="headers" :fields="sheet.fields"
                            v-model:mapping="columnMapping" :preview-rows="previewRows"
                            :enable-custom-fields="enableCustomFields" />
                    </template>
                    <SharedCSVImporterReviewStep v-else-if="step === 'review'" :records="records" :fields="mappedFields"
                        :custom-fields="customFieldHeaders" :sequence-provider="sequenceProvider" :disabled="busy || session?.stage !== 'review'"
                        :total-rows="session?.activeRowCount ?? 0" :invalid-rows="session?.invalidRowCount ?? 0"
                        :error-summaries="session?.errorSummary ?? []" :page="page" @update:page="changePage"
                        @update:records="updateRecords" @delete-row="deleteRow" @delete-error="deleteError" @revalidate="saveEdits" />
                </template>

                <!-- Optional slot for extra options in the review step (e.g. email validation) -->
                <div v-if="step === 'review' && !processing && $slots['review-options']" class="pt-3" :inert="busy || session?.stage !== 'review'">
                    <slot name="review-options" :mapped-field-keys="mappedFieldKeys" />
                </div>
            </div>

            <div v-if="session && !busy" class="flex gap-2 mt-3">
                <UButton v-if="submitError || session.stage === 'failed'" variant="outline" @click="refreshSession">Refresh status</UButton>
                <UButton v-if="!submissionStarted" variant="ghost" color="error" @click="cancelSession">Discard saved import</UButton>
            </div>
            <p v-if="submitError" role="alert" class="mt-3 text-sm text-red-600">{{ submitError }}</p>

            <template #footer>
                <div class="flex items-center justify-between">
                    <div class="text-sm text-slate-500">
                        <span v-if="step === 'upload' && session">
                            {{ session?.parsedRowCount }} rows detected
                        </span>
                        <span v-else-if="step === 'mapping'">
                            {{ mappedFieldCount }}/{{ requiredFieldCount }} required fields mapped
                            <span v-if="enableCustomFields && customFieldCount > 0" class="ml-2 text-blue-600">
                                + {{ customFieldCount }} custom
                            </span>
                        </span>
                        <span v-else-if="step === 'review'">
                            {{ validRowCount }}/{{ session?.activeRowCount ?? 0 }} rows valid
                            <span v-if="errorCount > 0" class="text-red-500 ml-2">
                                {{ errorCount }} errors
                            </span>
                        </span>
                    </div>
                    <div class="flex items-center space-x-2">
                        <UButton v-if="session && step !== 'upload' && !processing" color="neutral" variant="outline" :disabled="busy || processing || submissionStarted || session?.stage === 'failed'" @click="goBack">
                            Back
                        </UButton>
                        <UButton v-if="step === 'upload'" color="neutral" variant="outline" @click="handleClose">
                            Cancel
                        </UButton>
                        <UButton v-if="!processing && !loadingSessions && !sessionLookupFailed && (!savedSessions.length || session) && step !== 'review'" color="primary" @click="goNext" :disabled="!canProceed">
                            Next
                        </UButton>
                        <UButton v-if="step === 'review' && !processing" color="primary" @click="handleSubmit" :disabled="!canSubmit || busy"
                            :loading="busy">
                            {{ `Import ${validRowCount} rows` }}
                        </UButton>
                    </div>
                </div>
            </template>
        </UCard>
        </template>
    </UModal>
</template>

<script setup lang="ts">
import type { ImportOptions as CloudImportOptions, CreateImportInput, ImportResult as CloudImportResult, ImportReviewRowDTO as CloudImportRow, ImportSessionDTO as CloudImportSession } from '@coldreach/utils/cloud-imports';
import { fingerprintImportFile, uploadImportFile, isImportUploadComplete } from '~/utils/cloud-import-upload';

const props = defineProps<{
    sheet: SheetDef;
    cloud: Pick<CreateImportInput, 'targetKind' | 'campaignId' | 'chatId' | 'chatActionId'>;
    onComplete: (result: CloudImportResult, session: CloudImportSession) => void | Promise<void>;
    enableCustomFields?: boolean;
    sequenceProvider?: string | null;
}>();
const isOpen = defineModel<boolean>('open', { required: true });
const [signedInSession] = useSessionData();
const recoveryKey = computed(() => {
    const identity = signedInSession.value;
    if (!identity) return null;
    return `cloud-imports:${identity.orgId}:${identity.userId}:${props.cloud.targetKind}:${props.cloud.campaignId ?? ''}:${props.cloud.chatId ?? ''}:${props.cloud.chatActionId ?? ''}`;
});
const options = defineModel<CloudImportOptions>('options', { default: () => ({}) });
const session = ref<CloudImportSession | null>(null);
const savedSessions = ref<CloudImportSession[]>([]);
const loadingSessions = ref(false);
const sessionLookupFailed = ref(false);
const step = ref<'upload' | 'mapping' | 'review'>('upload');
const rawText = ref('');
const filename = ref<string | null>(null);
const selectedFile = shallowRef<File | null>(null);
const columnMapping = ref<Record<string, string>>({});
const records = ref<CloudImportRow[]>([]);
const previewRows = ref<string[][]>([]);
const page = ref(1);
const busy = ref(false);
const transferProgress = ref('');
const submitError = ref('');
const dirtyRows = new Set<number>();
let controller: AbortController | undefined;
let generation = 0;
let pollCount = 0;
let refreshing = false;
let completionDelivered = '';

const steps = ['upload', 'mapping', 'review'];
const currentStepIndex = computed(() => steps.indexOf(step.value));
const stepDescription = computed(() => ({ upload: 'Upload a CSV or Excel file, or paste data', mapping: 'Map columns to fields', review: 'Review and edit data before import' })[step.value]);
const headers = computed(() => session.value?.headers ?? []);
const processing = computed(() => !!transferProgress.value || ['parsing', 'validating', 'importing'].includes(session.value?.stage ?? ''));
const submissionStarted = computed(() => ['importing', 'completed'].includes(session.value?.stage ?? ''));
const progressDescription = computed(() => transferProgress.value || `${session.value?.stage === 'parsing' ? 'Reading file' : session.value?.stage === 'validating' ? 'Validating rows' : 'Importing rows'}… ${session.value?.importedRowCount || session.value?.parsedRowCount || ''}`);
const mappedFields = computed(() => {
    const keys = new Set(Object.values(columnMapping.value));
    return props.sheet.fields.filter(field => keys.has(field.key));
});
const mappedFieldKeys = computed(() => mappedFields.value.map(field => field.key));
const requiredFieldCount = computed(() => props.sheet.fields.filter(field => field.constraints?.some(c => c.type === 'required')).length);
const mappedFieldCount = computed(() => mappedFields.value.filter(field => field.constraints?.some(c => c.type === 'required')).length);
const customFieldHeaders = computed(() => Object.entries(columnMapping.value).filter(([, value]) => value === '__custom__').map(([header]) => header));
const customFieldCount = computed(() => customFieldHeaders.value.length);
const validRowCount = computed(() => (session.value?.activeRowCount ?? 0) - (session.value?.invalidRowCount ?? 0));
const errorCount = computed(() => session.value?.errorSummary.reduce((sum, error) => sum + error.count, 0) ?? 0);
const canProceed = computed(() => (session.value?.stage !== 'failed' || session.value.failureRetry === 'mapping') && !busy.value && !processing.value && (step.value === 'upload'
    ? !!selectedFile.value || !!rawText.value.trim() || session.value?.stage === 'mapping'
    : mappedFields.value.length > 0 && mappedFieldCount.value >= requiredFieldCount.value));
const canSubmit = computed(() => session.value?.stage === 'review' && validRowCount.value > 0 && session.value.invalidRowCount === 0 && errorCount.value === 0);

function selectFile(file: File) { selectedFile.value = file; }
watch(rawText, value => { if (value) selectedFile.value = null; });

function showError(error: unknown) {
    submitError.value = error instanceof Error ? error.message : 'The import could not be updated. Please retry.';
}

// Only session IDs are kept locally. The server still checks ownership on every
// read. This also recovers completion receipts that finished while the tab was closed.
function rememberedSessions(): string[] {
    if (!import.meta.client || !recoveryKey.value) return [];
    const stored = localStorage.getItem(recoveryKey.value);
    if (!stored) return [];
    try {
        const value: unknown = JSON.parse(stored);
        return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string' && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(id)) : [];
    } catch { return []; }
}
function rememberSession(id: string, keep: boolean) {
    if (!import.meta.client || !recoveryKey.value) return;
    const ids = new Set(rememberedSessions());
    if (keep) ids.add(id); else ids.delete(id);
    if (ids.size) localStorage.setItem(recoveryKey.value, JSON.stringify([...ids]));
    else localStorage.removeItem(recoveryKey.value);
}

async function loadRows() {
    if (!session.value) return;
    const id = session.value.id;
    const requestedPage = page.value;
    const result = await $fetch<{ rows: CloudImportRow[]; total: number }>(`/api/imports/${id}/rows`, { query: { offset: (requestedPage - 1) * 25, limit: 25 } });
    if (session.value?.id !== id || page.value !== requestedPage) return;
    records.value = result.rows;
    dirtyRows.clear();
}

async function acceptSession(value: CloudImportSession, restore = false) {
    if (!isOpen.value) return;
    session.value = value;
    if (restore) {
        columnMapping.value = Object.keys(value.columnMapping).length ? { ...value.columnMapping } : autoMapColumns(value.headers, props.sheet.fields);
        options.value = { ...value.options };
        page.value = 1;
    }
    if (value.stage === 'mapping' || value.failureRetry === 'mapping') {
        step.value = 'mapping';
        if (!Object.keys(columnMapping.value).length) columnMapping.value = autoMapColumns(value.headers, props.sheet.fields);
        const sample = await $fetch<{ rows: CloudImportRow[]; total: number }>(`/api/imports/${value.id}/rows`, { query: { offset: 0, limit: 3 } });
        if (session.value?.id === value.id && session.value.revision === value.revision) {
            previewRows.value = sample.rows.map(row => row.rawCells);
        }
    } else if (value.stage === 'uploading') step.value = 'upload';
    else if (['validating', 'review', 'importing', 'completed'].includes(value.stage)) step.value = 'review';
    if (value.stage === 'review') {
        page.value = Math.min(page.value, Math.max(1, Math.ceil(value.activeRowCount / 25)));
        await loadRows();
    }
    if (value.lastError) submitError.value = value.lastError;
    if (value.stage === 'completed' && value.result && completionDelivered !== value.id) {
        await props.onComplete(value.result, value);
        completionDelivered = value.id;
        rememberSession(value.id, false);
        isOpen.value = false;
    }
}

async function refreshSession() {
    if (!session.value || refreshing || busy.value) return;
    refreshing = true;
    const id = session.value.id;
    const currentGeneration = generation;
    try {
        const value = await $fetch<CloudImportSession>(`/api/imports/${id}`);
        if (currentGeneration !== generation || !isOpen.value) return;
        submitError.value = '';
        await acceptSession(value);
    } catch (error) { showError(error); }
    finally { refreshing = false; }
}

usePageInterval(async () => {
    if (!isOpen.value || !processing.value || busy.value || refreshing) return;
    if (++pollCount > 900) {
        submitError.value = 'This import is still processing. Close and reopen it to check again.';
        return;
    }
    await refreshSession();
}, { interval: 2000, immediate: true });

async function resumeSession(value: CloudImportSession) {
    busy.value = true;
    submitError.value = '';
    try {
        rememberSession(value.id, true);
        let current = await $fetch<CloudImportSession>(`/api/imports/${value.id}`);
        if (current.stage === 'uploading') {
            controller = new AbortController();
            const { uploadUrl } = await $fetch<{ uploadUrl: string }>(`/api/imports/${value.id}/upload`, { signal: controller.signal });
            if (await isImportUploadComplete(current.byteSize, uploadUrl, controller.signal)) {
                await $fetch(`/api/imports/${value.id}/uploaded`, { method: 'POST', signal: controller.signal });
                current = await $fetch<CloudImportSession>(`/api/imports/${value.id}`);
            }
        }
        await acceptSession(current, true);
    }
    catch (error) { showError(error); }
    finally { busy.value = false; }
}

async function goBack() {
    if (busy.value || processing.value || submissionStarted.value) return;
    dirtyRows.clear();
    if (step.value !== 'review' || !session.value) { step.value = 'upload'; return; }
    busy.value = true;
    try {
        const sample = await $fetch<{ rows: CloudImportRow[]; total: number }>(`/api/imports/${session.value.id}/rows`, { query: { offset: 0, limit: 3 } });
        previewRows.value = sample.rows.map(row => row.rawCells);
        step.value = 'mapping';
    } catch (error) { showError(error); }
    finally { busy.value = false; }
}

async function goNext() {
    if (!canProceed.value) return;
    if (step.value === 'mapping') { await saveMapping(); return; }
    if (session.value?.stage === 'mapping' && !selectedFile.value && !rawText.value.trim()) { step.value = 'mapping'; return; }
    const file = selectedFile.value ?? new File([rawText.value], 'pasted.csv', { type: 'text/csv' });
    if (!file.size) return;
    busy.value = true;
    submitError.value = '';
    controller = new AbortController();
    const currentGeneration = generation;
    try {
        transferProgress.value = 'Checking file contents…';
        const clientFingerprint = await fingerprintImportFile(file, controller.signal);
        let uploadUrl: string;
        if (session.value && session.value.stage !== 'uploading') {
            await $fetch(`/api/imports/${session.value.id}/cancel`, { method: 'POST' });
            rememberSession(session.value.id, false);
            session.value = null;
            columnMapping.value = {};
        }
        if (session.value) {
            if (session.value.clientFingerprint !== clientFingerprint) throw new Error('This is not the original file. Reselect the same file, or discard this import to start over.');
            ({ uploadUrl } = await $fetch<{ uploadUrl: string }>(`/api/imports/${session.value.id}/upload`, { signal: controller.signal }));
        } else {
            const created = await $fetch<{ id: string; uploadUrl: string }>('/api/imports', {
                method: 'POST', signal: controller.signal,
                body: { ...props.cloud, originalFilename: selectedFile.value?.name ?? null, contentType: file.type || (file.name.toLowerCase().endsWith('.xlsx') ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv'), byteSize: file.size, clientFingerprint, options: options.value },
            });
            uploadUrl = created.uploadUrl;
            rememberSession(created.id, true);
            session.value = await $fetch<CloudImportSession>(`/api/imports/${created.id}`, { signal: controller.signal });
        }
        await uploadImportFile(file, uploadUrl, controller.signal, bytes => { transferProgress.value = `Uploading ${Math.round(bytes / file.size * 100)}%…`; });
        await $fetch(`/api/imports/${session.value.id}/uploaded`, { method: 'POST', signal: controller.signal });
        if (currentGeneration !== generation) return;
        selectedFile.value = null;
        rawText.value = '';
        pollCount = 0;
        await acceptSession(await $fetch<CloudImportSession>(`/api/imports/${session.value.id}`), true);
    } catch (error) {
        if (!controller.signal.aborted) {
            if (!session.value) await loadSavedSessions();
            showError(error);
        }
    }
    finally { transferProgress.value = ''; busy.value = false; }
}

async function retryParsing() {
    if (!session.value || session.value.failureRetry !== 'parse' || busy.value) return;
    busy.value = true;
    submitError.value = '';
    try {
        await $fetch(`/api/imports/${session.value.id}/uploaded`, { method: 'POST' });
        pollCount = 0;
        await acceptSession(await $fetch<CloudImportSession>(`/api/imports/${session.value.id}`), true);
    } catch (error) { showError(error); }
    finally { busy.value = false; }
}

async function saveMapping(selectedWorksheet = session.value?.selectedWorksheet, resetMapping = false) {
    if (!session.value || busy.value) return;
    busy.value = true;
    submitError.value = '';
    try {
        await $fetch(`/api/imports/${session.value.id}/mapping`, { method: 'POST', body: {
            revision: session.value.revision, selectedWorksheet,
            columnMapping: resetMapping ? {} : columnMapping.value,
            customFieldHeaders: resetMapping ? [] : customFieldHeaders.value,
            options: options.value,
        } });
        if (resetMapping) columnMapping.value = {};
        page.value = 1;
        dirtyRows.clear();
        pollCount = 0;
        await acceptSession(await $fetch<CloudImportSession>(`/api/imports/${session.value.id}`));
    } catch (error) { showError(error); }
    finally { busy.value = false; }
}
async function changeWorksheet(index: number) { await saveMapping(index, true); }

function updateRecords(next: ImportRecord[]) {
    if (busy.value) return;
    records.value = records.value.map(row => {
        const updated = next.find(value => value.id === row.id);
        if (!updated) return row;
        if (Object.keys(updated.values).some(key => updated.values[key] !== row.values[key])) dirtyRows.add(row.sourceRowNumber);
        return { ...row, values: updated.values };
    });
}

async function mutateRows(change: { removeRowNumbers?: number[]; removeError?: string } = {}) {
    if (!session.value || busy.value) return false;
    const editableKeys = new Set([...mappedFieldKeys.value, ...customFieldHeaders.value]);
    const edits = records.value.filter(row => dirtyRows.has(row.sourceRowNumber)).map(row => ({
        sourceRowNumber: row.sourceRowNumber, rowRevision: row.rowRevision,
        // Validation may derive other fields (for example a company domain from
        // email). Those are server-owned; only send the mapped editable cells.
        values: Object.fromEntries(Object.entries(row.values).filter(([key]) => editableKeys.has(key))),
    }));
    if (!edits.length && !change.removeRowNumbers && !change.removeError) return true;
    busy.value = true;
    submitError.value = '';
    try {
        await $fetch(`/api/imports/${session.value.id}/rows`, { method: 'PATCH', body: { revision: session.value.revision, edits, ...change } });
        dirtyRows.clear();
        pollCount = 0;
        await acceptSession(await $fetch<CloudImportSession>(`/api/imports/${session.value.id}`));
        return true;
    } catch (error) { showError(error); return false; }
    finally { busy.value = false; }
}
async function saveEdits() { await mutateRows(); }
async function deleteRow(id: number) {
    const row = records.value.find(value => value.id === id);
    if (row) await mutateRows({ removeRowNumbers: [row.sourceRowNumber] });
}
async function deleteError(message: string) { await mutateRows({ removeError: message }); }
async function changePage(value: number) {
    if (busy.value || !await mutateRows() || processing.value) return;
    page.value = value;
    busy.value = true;
    try { await loadRows(); } catch (error) { showError(error); }
    finally { busy.value = false; }
}
async function handleSubmit() {
    if (busy.value || !await mutateRows() || !canSubmit.value) return;
    busy.value = true;
    submitError.value = '';
    try {
        await $fetch(`/api/imports/${session.value!.id}/submit`, { method: 'POST', body: { revision: session.value!.revision, options: options.value } });
        pollCount = 0;
        await acceptSession(await $fetch<CloudImportSession>(`/api/imports/${session.value!.id}`));
    } catch (error) { showError(error); }
    finally { busy.value = false; }
}
async function cancelSession() {
    if (!session.value || busy.value) return;
    busy.value = true;
    try {
        await $fetch(`/api/imports/${session.value.id}/cancel`, { method: 'POST' });
        rememberSession(session.value.id, false);
        session.value = null;
        if (props.cloud.targetKind === 'contacts') options.value = { enrichEmail: true, replaceInvalidEmail: true, verifyEmail: true };
        savedSessions.value = [];
        selectedFile.value = null;
        filename.value = null;
        rawText.value = '';
        columnMapping.value = {};
        submitError.value = '';
        step.value = 'upload';
    } catch (error) { showError(error); }
    finally { busy.value = false; }
}
function handleClose() { controller?.abort(); isOpen.value = false; }
onBeforeUnmount(() => { generation++; controller?.abort(); });
watch(isOpen, async open => {
    generation++;
    if (!open) { controller?.abort(); return; }
    pollCount = 0;
    submitError.value = '';
    if (session.value && completionDelivered !== session.value.id) { await resumeSession(session.value); return; }
    session.value = null;
    if (props.cloud.targetKind === 'contacts') options.value = { enrichEmail: true, replaceInvalidEmail: true, verifyEmail: true };
    step.value = 'upload';
    selectedFile.value = null;
    rawText.value = '';
    filename.value = null;
    columnMapping.value = {};
    await loadSavedSessions();
}, { immediate: true });

async function loadSavedSessions() {
    loadingSessions.value = true;
    sessionLookupFailed.value = false;
    submitError.value = '';
    const currentGeneration = generation;
    try {
        const available = await $fetch<CloudImportSession[]>('/api/imports', { query: props.cloud });
        const saved = available.filter(value => (value.chatId ?? undefined) === props.cloud.chatId && (value.chatActionId ?? undefined) === props.cloud.chatActionId);
        for (const id of rememberedSessions()) {
            if (saved.some(value => value.id === id)) continue;
            try {
                const value = await $fetch<CloudImportSession>(`/api/imports/${id}`);
                if (value.stage === 'cancelled') rememberSession(id, false);
                else saved.push(value);
            } catch (error) {
                const status = (error as { statusCode?: number }).statusCode;
                if (status === 404 || status === 403 || status === 410) rememberSession(id, false);
                else throw error;
            }
        }
        if (currentGeneration === generation) savedSessions.value = saved;
    } catch (error) { sessionLookupFailed.value = true; showError(error); }
    finally { loadingSessions.value = false; }
}
</script>
